import { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, type DriverProfile } from '../../lib/auth';
import { useSetPageHeader } from '../../components/app/AppShell';
import { apiFetch } from '../../lib/api-client';
import { env } from '../../lib/env';
import { Spinner } from '../../components/kit/Spinner';
import { haptic } from '../../lib/haptic';
import {
  getCachedDoc,
  putCachedDoc,
  invalidateDoc,
  type DocKey,
} from '../../lib/doc-cache';

const LazyDriverPdfViewer = lazy(() => import('../../components/form/DriverPdfViewer'));

const DOC_KEYS: Record<DocKey, { titleKey: string; backTo: string; updatedAtField: keyof DriverProfile }> = {
  photo:   { titleKey: 'kycDocs.photoTitle', backTo: '/profile/kyc',         updatedAtField: 'photoUpdatedAt' },
  dl:      { titleKey: 'kyc.dlTitle',        backTo: '/profile/kyc/dl',      updatedAtField: 'drivingLicenseUpdatedAt' },
  aadhaar: { titleKey: 'kyc.aadhaarTitle',   backTo: '/profile/kyc/aadhaar', updatedAtField: 'aadhaarUpdatedAt' },
  pan:     { titleKey: 'kyc.panTitle',       backTo: '/profile/kyc/pan',     updatedAtField: 'panUpdatedAt' },
};

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; mime: string; objectUrl: string }
  | { kind: 'empty' }
  | { kind: 'error'; message: string };

export function DocViewerRoute() {
  const { doc } = useParams<{ doc: DocKey }>();
  const { t } = useTranslation('profile');
  const conf = doc ? DOC_KEYS[doc] : undefined;
  useSetPageHeader(conf ? { backTo: conf.backTo, title: t(conf.titleKey) } : null, [doc]);

  const [state, setState] = useState<State>({ kind: 'loading' });
  const objectUrlRef = useRef<string | null>(null);
  // Race-cancellation token. Every load() call captures the latest value and
  // bails after every awaited boundary if its token is stale. Without this,
  // a Refresh tap mid-load (or fast back/forward navigation) could let an
  // earlier load resolve AFTER a later one — overwriting fresh state with
  // stale bytes and revoking the wrong objectURL.
  const loadTokenRef = useRef(0);

  const showBlob = (mime: string, blob: Blob) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(blob);
    objectUrlRef.current = url;
    setState({ kind: 'ready', mime, objectUrl: url });
  };

  const load = async (force = false) => {
    if (!doc || !conf) return;
    const myToken = ++loadTokenRef.current;
    setState({ kind: 'loading' });
    try {
      // Refresh /me first so the staleness comparison uses fresh timestamps.
      const me = await apiFetch<{ driver: DriverProfile }>('/driver-app/me');
      if (myToken !== loadTokenRef.current) return; // superseded
      useAuth.setState({ driver: me.driver });
      const updatedAtIso = me.driver[conf.updatedAtField] as string | null;
      const updatedAtMs = updatedAtIso ? new Date(updatedAtIso).getTime() : 0;

      const cached = !force ? await getCachedDoc(doc) : null;
      if (myToken !== loadTokenRef.current) return; // superseded
      if (cached && cached.cachedAt >= updatedAtMs) {
        showBlob(cached.mime, cached.blob);
        return;
      }

      const apiBase = env().VITE_API_BASE.replace(/\/+$/, '');
      const res = await fetch(`${apiBase}/v1/driver-app/me/kyc/${doc}/image`, { credentials: 'include' });
      if (myToken !== loadTokenRef.current) return; // superseded
      if (res.status === 404) { setState({ kind: 'empty' }); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const mime = res.headers.get('content-type') ?? 'application/octet-stream';
      const blob = await res.blob();
      if (myToken !== loadTokenRef.current) return; // superseded
      await putCachedDoc(doc, mime, blob);
      if (myToken !== loadTokenRef.current) return; // superseded
      showBlob(mime, blob);
    } catch (e: unknown) {
      if (myToken !== loadTokenRef.current) return; // superseded — don't even surface the error
      const message = e instanceof Error ? e.message : String(e);
      setState({ kind: 'error', message });
    }
  };

  useEffect(() => {
    void load();
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  if (!doc || !conf) return null;

  return (
    <div className="px-5 pt-2 pb-10">
      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={async () => { haptic.tap(); await invalidateDoc(doc); void load(true); }}
          className="text-sm font-semibold text-[var(--green-700)]"
        >
          {t('kycDocs.viewerRefresh')}
        </button>
      </div>

      {state.kind === 'loading' ? (
        <div className="flex flex-col items-center gap-3 py-12">
          <Spinner size={32} />
          <p className="text-sm text-[var(--color-text-secondary)]">{t('kycDocs.viewerLoading')}</p>
        </div>
      ) : null}

      {state.kind === 'empty' ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-sm text-[var(--color-text-secondary)]">{t('kycDocs.viewerEmpty')}</p>
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-sm text-[var(--color-text-secondary)] mb-1">{t('kycDocs.viewerError')}</p>
          <p className="text-xs text-[var(--color-text-secondary)]">{state.message}</p>
        </div>
      ) : null}

      {state.kind === 'ready' && state.mime.startsWith('image/') ? (
        <img
          src={state.objectUrl}
          alt={t(conf.titleKey)}
          style={{ width: '100%', maxHeight: '85vh', objectFit: 'contain' }}
        />
      ) : null}

      {state.kind === 'ready' && state.mime === 'application/pdf' ? (
        <Suspense fallback={<Spinner size={32} />}>
          <LazyDriverPdfViewer file={state.objectUrl} />
        </Suspense>
      ) : null}
    </div>
  );
}
